import styled from "styled-components";

const FONT_BODY = '"Manrope", "IBM Plex Sans", "Avenir Next", sans-serif';
const FONT_DISPLAY = '"Space Grotesk", "IBM Plex Sans", "Avenir Next", sans-serif';
const FONT_EDITORIAL = '"DM Serif Text", "Bodoni Moda", serif';

export const PageRoot = styled.main`
  min-height: 100vh;
  background: #f3faf5;
  color: #2b4a3b;
  font-family: ${FONT_BODY};
`;

export const SectionContainer = styled.div`
  width: min(1160px, calc(100% - 48px));
  margin: 0 auto;

  @media (max-width: 992px) {
    width: min(1160px, calc(100% - 34px));
  }
`;

export const HeroSection = styled.section`
  padding: clamp(142px, 16vw, 176px) 0 clamp(64px, 8vw, 88px);
  border-top: 1px solid #d2e5d8;
  border-bottom: 1px solid #d2e5d8;
  background: url("/images/fundo verde.jpg") no-repeat center center / cover;
`;

export const HeroEyebrow = styled.div`
  font-family: ${FONT_DISPLAY};
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.9);
`;

export const HeroTitle = styled.h1`
  margin: 10px 0 12px;
  max-width: 16ch;
  font-family: ${FONT_DISPLAY};
  font-size: clamp(2rem, 4.8vw, 4rem);
  line-height: 0.96;
  letter-spacing: -0.02em;
  color: #ffffff;
`;

export const HeroText = styled.p`
  margin: 0;
  max-width: 72ch;
  font-family: ${FONT_BODY};
  font-size: clamp(0.98rem, 1.2vw, 1.04rem);
  line-height: 1.72;
  color: rgba(245, 250, 247, 0.92);
`;

export const HeroActions = styled.div`
  margin-top: 22px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const buttonBase = `
  min-height: 42px;
  padding: 0 16px;
  border-radius: 999px;
  font-family: ${FONT_DISPLAY};
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  :hover {
    transform: translateY(-1px);
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase}
  border: 1px solid #6c9f85;
  background: #6c9f85;
  color: #f7faf8;

  :hover {
    background: #5b8d74;
    border-color: #5b8d74;
  }
`;

export const SecondaryButton = styled.button`
  ${buttonBase}
  border: 1px solid #cde1d4;
  background: #ffffff;
  color: #567668;

  :hover {
    border-color: #a9c6b6;
    background: #f8fdf9;
  }
`;

export const InfoSection = styled.section`
  padding: clamp(58px, 8vw, 82px) 0 24px;
`;

export const InfoCard = styled.article`
  border-radius: 14px;
  border: 1px solid #cde1d4;
  background: #f8fdf9;
  box-shadow: 0 2px 8px rgba(24, 36, 29, 0.04);
  padding: clamp(18px, 2.8vw, 30px);
`;

export const InfoTitle = styled.h2`
  margin: 0 0 10px;
  font-family: ${FONT_EDITORIAL};
  font-size: clamp(1.5rem, 2.8vw, 2.2rem);
  line-height: 1.1;
  letter-spacing: -0.01em;
  color: #2a4a3b;
`;

export const InfoText = styled.p`
  margin: 0;
  max-width: 74ch;
  font-size: 0.98rem;
  line-height: 1.72;
  color: #5f796c;
`;

export const HighlightList = styled.ul`
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const HighlightItem = styled.li`
  min-height: 32px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid #bcd8c9;
  background: #eef7f1;
  font-family: ${FONT_DISPLAY};
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #537969;
`;

export const AlbumSection = styled.section`
  padding: clamp(54px, 7vw, 78px) 0 clamp(80px, 9vw, 102px);
`;

export const AlbumHeader = styled.div`
  margin-bottom: 20px;
  max-width: 76ch;
`;

export const AlbumEyebrow = styled.div`
  font-family: ${FONT_DISPLAY};
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #6d897a;
`;

export const AlbumTitle = styled.h2`
  margin: 8px 0 8px;
  font-family: ${FONT_EDITORIAL};
  font-size: clamp(1.6rem, 3vw, 2.3rem);
  line-height: 1.08;
  letter-spacing: -0.01em;
  color: #2a4a3b;
`;

export const AlbumText = styled.p`
  margin: 0;
  font-size: 0.96rem;
  line-height: 1.66;
  color: #5f796c;
`;

export const AlbumGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 1080px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

export const AlbumCard = styled.figure`
  margin: 0;
  grid-column: span 3;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #cde1d4;
  background: #f8fdf9;
  box-shadow: 0 2px 8px rgba(24, 36, 29, 0.04);

  @media (max-width: 1080px) {
    grid-column: span 1;
  }
`;

export const AlbumImage = styled.img`
  width: 100%;
  aspect-ratio: 4 / 5;
  object-fit: cover;
  display: block;
`;

export const AlbumCaption = styled.figcaption`
  min-height: 38px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  font-family: ${FONT_DISPLAY};
  font-size: 0.64rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #628476;
`;

export const EmptyState = styled.section`
  padding: 180px 0 100px;
`;

export const EmptyCard = styled.div`
  border-radius: 14px;
  border: 1px solid #cde1d4;
  background: #f8fdf9;
  padding: 28px;
`;

export const EmptyTitle = styled.h1`
  margin: 0 0 8px;
  font-family: ${FONT_DISPLAY};
  font-size: clamp(1.5rem, 2.8vw, 2rem);
  color: #2a4a3b;
`;

export const EmptyText = styled.p`
  margin: 0;
  color: #5f796c;
  line-height: 1.62;
`;
